import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';
import { roundTo } from '@/utils';
import useRequestAnimationFrameLoop from '@/hooks/use-request-animation-frame-loop';
import useSound from '@/hooks/use-sound';
import Space from '@/components/Space';
import GlowingPlayButton from '@/components/GlowingPlayButton';
import Spacer from '@/components/Spacer';

import { FPS } from './DeferredValueTimeline.constants';
import RoundedButtonWrapper from './RoundedButtonWrapper';
import Timeline from './Timeline';
import Instructions from './Instructions';
import { Render, Video, Status } from './DeferredValueTimeline.types';

interface Props {
  video: Video;
  renders: Array<Render>;
  clicks: Array<number>;
  showInstructions?: boolean;
}

function DeferredValueTimeline({
  video,
  renders,
  clicks,
  showInstructions,
}: Props) {
  const id = React.useId();

  const [status, setStatus] = React.useState<Status>('idle');
  const [currentFrame, setCurrentFrame] = React.useState<number>(0);
  const [playMouseClick] = useSound('/sounds/mouse-click.wav', {
    volume: 0.45,
  });

  const mouseClicksPlayed = React.useRef<Record<string, boolean>>({});

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const lastFrameTimeRef = React.useRef<number>(Date.now());

  const totalNumOfFrames = video.length;
  const finalFrame = totalNumOfFrames - 1;

  const currentFrameRef = React.useRef(currentFrame);
  currentFrameRef.current = currentFrame;

  useRequestAnimationFrameLoop(() => {
    const now = Date.now();
    const timeElapsed = now - lastFrameTimeRef.current;
    lastFrameTimeRef.current = now;

    if (status === 'playing') {
      const numOfFrames = (timeElapsed / 1000) * FPS;

      const currentFrame = currentFrameRef.current;

      let nextFrame = Math.min(
        currentFrame + numOfFrames,
        finalFrame
      );

      // On high-refresh-rate monitors, this requestAnimationFrame loop runs faster than 60FPS. To keep the timeline smooth, we will use decimal values for `currentFrame`. It might be 45.5 or 45.2, etc. We’ll round to 2 decimal places, just to keep it from being a huge number.
      nextFrame = roundTo(nextFrame, 2);

      // The only time this is a problem is at the very end, when we reach the end of the video. This leads to a situation where the video is ended, the timeline APPEARS to be at the end, but when the user goes to restart, it plays for the briefest flicker, for a single half-frame. When we're near the end, round up.
      if (Math.ceil(nextFrame) >= finalFrame) {
        nextFrame = Math.ceil(nextFrame);
      }

      // We want to play an SFX when the click happens. If we play the sound RIGHT on the frame, it sound a bit late, so we'll play it 3 frames early/
      // Annoyingly, requestAnimationFrame is too fast on high-refresh-rate monitors, faster than the associated video. To prevent playing the SFX 2-3+ times, we'll track which frames have already played the sound.
      const matchedFrameForClick = Math.round(nextFrame + 3);
      const hasAlreadyPlayedThisFrame =
        mouseClicksPlayed.current[matchedFrameForClick];

      if (
        !hasAlreadyPlayedThisFrame &&
        clicks.includes(matchedFrameForClick)
      ) {
        playMouseClick();
        mouseClicksPlayed.current[matchedFrameForClick] = true;
      }

      setCurrentFrame(nextFrame);
    }
  });

  // HACK: So, I want the "Space" key to play/pause the video. The play button isn't focused, though, and it turns out, dragging the sweeper doesn't focus it either. So: whenever the video is scrubbed, we’ll set a global variable that tracks the ID of this timeline. When the user hits "Space", whichever timeline was most recently interacted with will be toggled.
  // Ideally, this should use React context, but there is no shared provider, and honestly I think this is fine.
  React.useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      // @ts-ignore
      if (window.mostRecentlyToggledTimeline !== id) {
        return;
      }

      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();

        setStatus((currentStatus) => {
          if (currentStatus === 'playing') {
            videoRef.current?.pause();
            return 'paused';
          } else {
            videoRef.current?.play();
            return 'playing';
          }
        });
      }
    }

    window.addEventListener('keydown', handleKeydown, {
      capture: true,
    });

    return () => {
      window.removeEventListener('keydown', handleKeydown, {
        capture: true,
      });
    };
  }, [id]);

  // As we scrub around, when the video is paused, we need to jump the video to the appropriate frame.
  React.useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.currentTime = currentFrame / FPS;
  }, [currentFrame]);

  // Chrome extensions and stuff can auto-change playback speed. Let's try and keep it at 1x, to prevent bugs.
  React.useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.playbackRate = 1;
  }, [currentFrame]);

  React.useEffect(() => {
    if (status === 'playing') {
      mouseClicksPlayed.current = {};
    }
  }, [status]);

  return (
    <Wrapper>
      <VideoWrapper
        style={{
          '--video-lightness':
            status === 'idle'
              ? showInstructions
                ? '0.75'
                : '0.4'
              : '0',
        }}
      >
        <video
          playsInline
          ref={videoRef}
          onEnded={() => {
            setStatus('done');
          }}
        >
          <source src={video.src + '.ogg'} type="video/ogg" />
          <source src={video.src + '.mp4'} type="video/mp4" />
        </video>
        {showInstructions && (
          <Instructions isDismissed={status !== 'idle'} />
        )}
      </VideoWrapper>
      <ActionsRow>
        <RoundedButtonWrapper />
        <PlayButtonWrapper>
          <GlowingPlayButton
            isGlowing={status === 'idle'}
            status={status}
            onClick={() => {
              if (status === 'playing') {
                videoRef.current?.pause();
                setStatus('paused');
                setCurrentFrame(Math.round(currentFrame));
              } else {
                videoRef.current?.play();
                setStatus('playing');

                if (currentFrame >= finalFrame) {
                  setCurrentFrame(0);
                }
              }
            }}
          />
        </PlayButtonWrapper>
      </ActionsRow>
      <Spacer size={0} />
      <Timeline
        id={id}
        renders={renders}
        clicks={clicks}
        currentFrame={currentFrame}
        totalNumOfFrames={totalNumOfFrames}
        setCurrentFrame={setCurrentFrame}
        handleScrubStart={() => {
          if (status !== 'paused') {
            videoRef.current?.pause();
            setStatus('paused');
          }
        }}
        handleTogglePlay={() => {
          if (status === 'playing') {
            videoRef.current?.pause();
            setStatus('paused');
          } else {
            videoRef.current?.play();
            setStatus('playing');
          }
        }}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Space)`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: var(--viewport-padding);
  margin-inline: calc(var(--viewport-padding) * -1);
  margin-block: 32px 64px;
  border-radius: 12px;
  isolation: isolate;
  user-select: none;

  @media ${BREAKPOINTS.smAndSmaller} {
    border-radius: 0px;
  }

  html[data-color-mode='dark'] & {
    border: 2px solid var(--color-gray-100);
    @media ${BREAKPOINTS.mdAndSmaller} {
      border-left: none;
      border-right: none;
      border-radius: 0;
    }
  }
`;

const VideoWrapper = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 8px 24px 32px;
  border-radius: 4px;
  background: hsl(0deg 0% 100%);
  cursor: not-allowed;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: black;
    opacity: var(--video-lightness);
    transition: opacity 200ms;
  }

  video {
    width: 417px;
    aspect-ratio: 834 / 600;
  }
`;

const ActionsRow = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 0px;
  transform: translateY(-16px);
`;

const PlayButtonWrapper = styled.div`
  padding: 16px;
  border-radius: 1000px;
`;

export default DeferredValueTimeline;
